import React, { useEffect, useState, useRef, Children } from 'react';
import { TagIcon, SearchIcon, CalendarIcon, PercentIcon, DollarSignIcon, MegaphoneIcon, ChevronDownIcon, HeartIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
import { mockBooks } from '../../../data/productManagerData';
import type { PM } from './useProductManager';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
// Mock data for anthologies
const mockAnthologies = [{
  id: 'anth-1',
  title: 'Hearts for Hope',
  genre: 'Romance'
}, {
  id: 'anth-2',
  title: 'Voices Unbound',
  genre: 'Literary Fiction'
}, {
  id: 'anth-3',
  title: 'Midnight Tales for Change',
  genre: 'Dark Romance'
}];
// Mock data for nonprofits
const mockNonprofits = [{
  id: 'np-1',
  name: "Children's Literacy Foundation",
  category: 'Literacy',
  verified: true
}, {
  id: 'np-2',
  name: 'Book Access for All',
  category: 'Education',
  verified: true
}, {
  id: 'np-3',
  name: 'Rainbow Reads Initiative',
  category: 'LGBTQ+',
  verified: true
}, {
  id: 'np-4',
  name: 'Mental Health Through Stories',
  category: 'Mental Health',
  verified: false
}, {
  id: 'np-5',
  name: 'Libraries First',
  category: 'Libraries',
  verified: true
}, {
  id: 'np-6',
  name: 'Rural Library Project',
  category: 'Libraries',
  verified: false
}];
interface SaleFormSectionProps {
  pm: PM;
}
export const SaleFormSection: React.FC<SaleFormSectionProps> = ({
  pm
}) => {
  const dk = useDarkClasses();
  const {
    saleForm,
    setSaleForm
  } = pm;
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [anthologySearch, setAnthologySearch] = useState('');
  const [showAnthologyDropdown, setShowAnthologyDropdown] = useState(false);
  const [nonprofitSearch, setNonprofitSearch] = useState('');
  const [showNonprofitDropdown, setShowNonprofitDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const anthologyDropdownRef = useRef<HTMLDivElement>(null);
  const nonprofitDropdownRef = useRef<HTMLDivElement>(null);
  // Filter books for search
  const filteredBooks = mockBooks.filter(book => book.title.toLowerCase().includes(productSearch.toLowerCase()) || book.author.toLowerCase().includes(productSearch.toLowerCase()));
  // Filter anthologies for search
  const filteredAnthologies = mockAnthologies.filter(anth => anth.title.toLowerCase().includes(anthologySearch.toLowerCase()) || anth.genre.toLowerCase().includes(anthologySearch.toLowerCase()));
  // Filter nonprofits for search
  const filteredNonprofits = mockNonprofits.filter(np => np.name.toLowerCase().includes(nonprofitSearch.toLowerCase()) || np.category.toLowerCase().includes(nonprofitSearch.toLowerCase()));
  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
      if (anthologyDropdownRef.current && !anthologyDropdownRef.current.contains(event.target as Node)) {
        setShowAnthologyDropdown(false);
      }
      if (nonprofitDropdownRef.current && !nonprofitDropdownRef.current.contains(event.target as Node)) {
        setShowNonprofitDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Get selected product display name
  const getSelectedProductName = () => {
    if (!saleForm.productId) return '';
    const book = mockBooks.find(b => b.id === saleForm.productId);
    return book ? `${book.title} by ${book.author}` : '';
  };
  // Get selected anthology display name
  const getSelectedAnthologyName = () => {
    if (!saleForm.anthologyId) return '';
    const anth = mockAnthologies.find(a => a.id === saleForm.anthologyId);
    return anth ? anth.title : '';
  };
  // Get selected nonprofit display name
  const getSelectedNonprofitName = () => {
    if (!saleForm.nonprofitId) return '';
    const np = mockNonprofits.find(n => n.id === saleForm.nonprofitId);
    return np ? np.name : '';
  };
  const isAnthologyDonation = saleForm.dealType === 'anthology_donation';
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-21846" data-mp="2oy7joliqiobm">
      <h2 className={`text-lg font-bold mb-6 flex items-center ${dk.textHeading}`} data-id="element-21847" data-mp="32x6yz2kcxqv4">
        <TagIcon className="w-5 h-5 mr-2 text-orange-500" data-id="element-21848" data-mp="2yukg3r5nzga2" />
        {isAnthologyDonation ? 'Anthology Donation Details' : 'Sale Details'}
      </h2>

      <div className="space-y-8" data-id="element-21849" data-mp="i4n214o44wc6">
        {/* 1. Deal Type - Now with 3 options */}
        <div data-id="element-21850" data-mp="e43809yu68u9">
          <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-21851" data-mp="e4lbnrntvpc7">
            Deal Type
          </label>
          <div className="flex flex-col sm:flex-row gap-3 mb-4" data-id="element-21852" data-mp="qo03cagpry6o">
            <button onClick={() => setSaleForm({
            ...saleForm,
            dealType: 'self'
          })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.dealType === 'self' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21853" data-mp="qle02n0uw4u9">
              <TagIcon className="w-4 h-4" data-id="element-21854" data-mp="zn3nzivj4lna" />
              <span className="font-medium" data-id="element-21855" data-mp="zm2ehiu5w77o">Self-Run Sale</span>
            </button>
            <button onClick={() => setSaleForm({
            ...saleForm,
            dealType: 'platform'
          })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.dealType === 'platform' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21856" data-mp="1nceu7fvnf88t">
              <MegaphoneIcon className="w-4 h-4" data-id="element-21857" data-mp="1j1u3nhq2l9oz" />
              <span className="font-medium" data-id="element-21858" data-mp="1j3naoy4lhvxi">Platform Deal</span>
            </button>
            <button onClick={() => setSaleForm({
            ...saleForm,
            dealType: 'anthology_donation',
            donationScope: saleForm.donationScope || 'this_anthology',
            donationDuration: saleForm.donationDuration || 'set_period'
          })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.dealType === 'anthology_donation' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21859" data-mp="1e9j7mpyga81p">
              <HeartIcon className="w-4 h-4" data-id="element-21860" data-mp="3kz4bvvu4lve4" />
              <span className="font-medium" data-id="element-21861" data-mp="3kxta21k8qxpn">Anthology Donation</span>
            </button>
          </div>

          {saleForm.dealType === 'platform' && <div className="animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-21862" data-mp="2t7ybdeahtvob">
              <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21863" data-mp="2t5keczw969e4">
                Platform Name
              </label>
              <input type="text" value={saleForm.platformName || ''} onChange={e => setSaleForm({
            ...saleForm,
            platformName: e.target.value
          })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. Bookbub, StoryOrigin, Chirp..." data-id="element-21864" data-mp="3211rjuciwmmn" />
            </div>}
        </div>

        {/* Anthology Donation Form */}
        {isAnthologyDonation && <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-21865" data-mp="17v6pylojlzuj">
            {/* Anthology Selector */}
            <div data-id="element-21866" data-mp="1lu6dfq0bk8p4">
              <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21867" data-mp="1ltocwh6mj8be">
                Select Anthology
              </label>
              <div className="relative" ref={anthologyDropdownRef} data-id="element-21868" data-mp="1vjzehx2u55f4">
                <div className={`w-full p-2 rounded-md border flex items-center justify-between cursor-pointer ${dk.inputOnCard}`} onClick={() => setShowAnthologyDropdown(!showAnthologyDropdown)} data-id="element-21869" data-mp="1qlo4nmcjiuww">
                  <span className={saleForm.anthologyId ? dk.textPrimary : dk.textMuted} data-id="element-21870" data-mp="2j8xlefx26vuo">
                    {getSelectedAnthologyName() || 'Select an anthology...'}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 ${dk.textMuted}`} data-id="element-21871" data-mp="2aa3t31qrgrmx" />
                </div>

                {showAnthologyDropdown && <div className={`absolute z-10 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto border ${dk.card} ${dk.border}`} data-id="element-21872" data-mp="20hy2nwg5qi9u">
                    <div className={`p-2 sticky top-0 border-b border-gray-100 dark:border-gray-700 ${dk.card}`} data-id="element-21873" data-mp="1rhrt2lnky2fy">
                      <div className="relative" data-id="element-21874" data-mp="1vjd1nut2zukc">
                        <SearchIcon className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${dk.textMuted}`} data-id="element-21875" data-mp="1hisigxj97wqb" />
                        <input type="text" value={anthologySearch} onChange={e => setAnthologySearch(e.target.value)} placeholder="Search anthologies..." className={`w-full p-2 pl-7 text-sm rounded-md border ${dk.inputOnCard}`} autoFocus data-id="element-21876" data-mp="1mkhy6qhf8ykq" />
                      </div>
                    </div>
                    {filteredAnthologies.length > 0 ? filteredAnthologies.map(anth => <div key={anth.id} onClick={() => {
                setSaleForm({
                  ...saleForm,
                  anthologyId: anth.id,
                  anthologyTitle: anth.title
                });
                setShowAnthologyDropdown(false);
                setAnthologySearch('');
              }} className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b last:border-0 ${dk.border}`} data-id="element-21877" data-mp="3b9sim9m78l4f">
                          <div className={`font-medium text-sm ${dk.textPrimary}`} data-id="element-21878" data-mp="1rah3kmnh692d">
                            {anth.title}
                          </div>
                          <div className={`text-xs ${dk.textMuted}`} data-id="element-21879" data-mp="zl5kvajthlcf">
                            {anth.genre}
                          </div>
                        </div>) : <div className={`p-4 text-center text-sm ${dk.textMuted}`} data-id="element-21880" data-mp="18mc0ziik0u55">
                        No anthologies found
                      </div>}
                  </div>}
              </div>
            </div>

            {/* Donation Scope */}
            <div data-id="element-21881" data-mp="1cnynf9uxdm3i">
              <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-21882" data-mp="1cohu1uf4ko28">
                Donation Scope
              </label>
              <div className="flex gap-3" data-id="element-21883" data-mp="1lodaoerg9b9n">
                <button onClick={() => setSaleForm({
              ...saleForm,
              donationScope: 'this_anthology'
            })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.donationScope === 'this_anthology' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21884" data-mp="1lpogsl8dav3x">
                  <span className="font-medium text-sm" data-id="element-21885" data-mp="a8oovqpxmiaf">
                    This Anthology Only
                  </span>
                </button>
                <button onClick={() => setSaleForm({
              ...saleForm,
              donationScope: 'all_sales'
            })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.donationScope === 'all_sales' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21886" data-mp="7ojniksm6ba4">
                  <span className="font-medium text-sm" data-id="element-21887" data-mp="3b58wsejkzz3h">All My Sales</span>
                </button>
              </div>
            </div>

            {/* Donation Percentage */}
            <div data-id="element-21888" data-mp="364ckbcl5z4mg">
              <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21889" data-mp="324u60znulrsl">
                Donation Percentage
              </label>
              <div className="flex flex-wrap gap-2 mb-3" data-id="element-21890" data-mp="2x7u1it6ncx3l">
                {[5, 10, 25, 50, 100].map(pct => <button key={pct} onClick={() => setSaleForm({
              ...saleForm,
              donationPercentage: pct
            })} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${saleForm.donationPercentage === pct ? 'bg-rose-500 text-white' : `bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21891" data-mp="1546j2gy69w3h">
                    {pct}%
                  </button>)}
              </div>
              <div className="relative max-w-[200px]" data-id="element-21892" data-mp="dciam5iiltpx">
                <input type="number" min="1" max="100" value={saleForm.donationPercentage || ''} onChange={e => setSaleForm({
              ...saleForm,
              donationPercentage: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
            })} className={`w-full p-2 pr-8 rounded-md border ${dk.inputOnCard}`} placeholder="25" data-id="element-21893" data-mp="df4ffxe0tv4g" />
                <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${dk.textMuted}`} data-id="element-21894" data-mp="30lhe5d99a99w">
                  %
                </span>
              </div>
            </div>

            {/* Duration */}
            <div data-id="element-21895" data-mp="39l4avlr1iqwm">
              <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-21896" data-mp="39lm9uq537j88">
                Duration
              </label>
              <div className="flex gap-3 mb-4" data-id="element-21897" data-mp="1f7cxqc9i5owf">
                <button onClick={() => setSaleForm({
              ...saleForm,
              donationDuration: 'set_period'
            })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.donationDuration === 'set_period' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21898" data-mp="1fa7he1koz5os">
                  <CalendarIcon className="w-4 h-4" data-id="element-21899" data-mp="3t8b0o9cbbzld" />
                  <span className="font-medium text-sm" data-id="element-21900" data-mp="22sw8lcjs7yg">Set Period</span>
                </button>
                <button onClick={() => setSaleForm({
              ...saleForm,
              donationDuration: 'permanent',
              endDate: ''
            })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.donationDuration === 'permanent' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21901" data-mp="6vlzf4sdb88f">
                  <HeartIcon className="w-4 h-4" data-id="element-21902" data-mp="1wdso5127kxrs" />
                  <span className="font-medium text-sm" data-id="element-21903" data-mp="1rdn1y0epk3h3">
                    Permanent / Ongoing
                  </span>
                </button>
              </div>

              {saleForm.donationDuration === 'set_period' ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-21904" data-mp="2k22due2jm5o7">
                  <div data-id="element-21905" data-mp="2f9irarlvo079">
                    <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21906" data-mp="2f8prkalc6aym">
                      Start Date
                    </label>
                    <div className="relative" data-id="element-21907" data-mp="37sfw5cvbsq5l">
                      <CalendarIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dk.textMuted}`} data-id="element-21908" data-mp="330emzztgffjs" />
                      <input type="date" value={saleForm.startDate || ''} onChange={e => setSaleForm({
                  ...saleForm,
                  startDate: e.target.value
                })} className={`w-full p-2 pl-9 rounded-md border ${dk.inputOnCard}`} data-id="element-21909" data-mp="2bb8u8to6mrs6" />
                    </div>
                  </div>
                  <div data-id="element-21910" data-mp="2yzs59gym9iny">
                    <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21911" data-mp="2l2wi6klj5qow">
                      End Date
                    </label>
                    <div className="relative" data-id="element-21912" data-mp="19nhllhx8h0ub">
                      <CalendarIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dk.textMuted}`} data-id="element-21913" data-mp="19ln1u6xp7ert" />
                      <input type="date" value={saleForm.endDate || ''} onChange={e => setSaleForm({
                  ...saleForm,
                  endDate: e.target.value
                })} className={`w-full p-2 pl-9 rounded-md border ${dk.inputOnCard}`} data-id="element-21914" data-mp="164r5ln69qg9k" />
                    </div>
                  </div>
                </div> : <div className={`flex items-start gap-2 p-3 rounded-lg ${dk.isDark ? 'bg-rose-900/20' : 'bg-rose-50'}`} data-id="element-21915" data-mp="idmv2hj0dlj7">
                  <InfoIcon className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" data-id="element-21916" data-mp="sdm6qnaq5s0a" />
                  <div data-id="element-21917" data-mp="4nrqdvr75lba">
                    <p className={`text-sm ${dk.isDark ? 'text-rose-300' : 'text-rose-700'}`} data-id="element-21918" data-mp="4n08fnqivm3h">
                      This donation commitment will run indefinitely until you
                      manually end it.
                    </p>
                    <div className="mt-2" data-id="element-21919" data-mp="2nc4oc6qgh7rd">
                      <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21920" data-mp="2nd6ap7cx9goe">
                        Start Date
                      </label>
                      <div className="relative max-w-[200px]" data-id="element-21921" data-mp="2x7g48sdlol0p">
                        <CalendarIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dk.textMuted}`} data-id="element-21922" data-mp="3azwdwc10bhal" />
                        <input type="date" value={saleForm.startDate || ''} onChange={e => setSaleForm({
                    ...saleForm,
                    startDate: e.target.value
                  })} className={`w-full p-2 pl-9 rounded-md border ${dk.inputOnCard}`} data-id="element-21923" data-mp="3kytvsw3xgckm" />
                      </div>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Nonprofit Selector */}
            <div data-id="element-21924" data-mp="3nj9sgz4z0oak">
              <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21925" data-mp="3nhp6vz0mwfou">
                Select Nonprofit
              </label>
              <div className="relative" ref={nonprofitDropdownRef} data-id="element-21926" data-mp="3ehacma5ozq97">
                <div className={`w-full p-2 rounded-md border flex items-center justify-between cursor-pointer ${dk.inputOnCard}`} onClick={() => setShowNonprofitDropdown(!showNonprofitDropdown)} data-id="element-21927" data-mp="3ikoznks1t6w5">
                  <span className={saleForm.nonprofitId ? dk.textPrimary : dk.textMuted} data-id="element-21928" data-mp="12t1701k2i0j5">
                    {getSelectedNonprofitName() || 'Select a nonprofit organization...'}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 ${dk.textMuted}`} data-id="element-21929" data-mp="11b3wrcumo8aq" />
                </div>

                {showNonprofitDropdown && <div className={`absolute z-10 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto border ${dk.card} ${dk.border}`} data-id="element-21930" data-mp="wf53pbycu23z">
                    <div className={`p-2 sticky top-0 border-b border-gray-100 dark:border-gray-700 ${dk.card}`} data-id="element-21931" data-mp="1p16nnpmt387r">
                      <div className="relative" data-id="element-21932" data-mp="1fbenmfvxtmvw">
                        <SearchIcon className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${dk.textMuted}`} data-id="element-21933" data-mp="1fa3myoxsgv90" />
                        <input type="text" value={nonprofitSearch} onChange={e => setNonprofitSearch(e.target.value)} placeholder="Search by name or category..." className={`w-full p-2 pl-7 text-sm rounded-md border ${dk.inputOnCard}`} autoFocus data-id="element-21934" data-mp="27t9696j0cykn" />
                      </div>
                    </div>
                    {filteredNonprofits.length > 0 ? filteredNonprofits.map(np => <div key={np.id} onClick={() => {
                setSaleForm({
                  ...saleForm,
                  nonprofitId: np.id,
                  nonprofitName: np.name,
                  nonprofitCategory: np.category
                });
                setShowNonprofitDropdown(false);
                setNonprofitSearch('');
              }} className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b last:border-0 ${dk.border}`} data-id="element-21935" data-mp="2u9sm1upavcyk">
                          <div className="flex items-center justify-between" data-id="element-21936" data-mp="dkos2jubjtfu">
                            <div className={`font-medium text-sm ${dk.textPrimary}`} data-id="element-21937" data-mp="sannwfyeiss5">
                              {np.name}
                            </div>
                            {np.verified && <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-21938" data-mp="3qrzxuphb9h21" />}
                          </div>
                          <div className="flex items-center gap-2 mt-1" data-id="element-21939" data-mp="8ng9wj967pel">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-21940" data-mp="4ka4texso49p">
                              {np.category}
                            </span>
                          </div>
                        </div>) : <div className={`p-4 text-center text-sm ${dk.textMuted}`} data-id="element-21941" data-mp="1sj3vf72g9f1x">
                        No nonprofits found
                      </div>}
                  </div>}
              </div>
            </div>
          </div>}

        {/* Standard Sale Form (only show when not anthology donation) */}
        {!isAnthologyDonation && <>
            {/* Product Selection */}
            <div data-id="element-21942" data-mp="1wkfk9adi1w92">
              <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21943" data-mp="1wkxpgwi53kt4">
                Select Product
              </label>
              <div className="relative" ref={dropdownRef} data-id="element-21944" data-mp="2p8tmk2bmfeqf">
                <div className={`w-full p-2 rounded-md border flex items-center justify-between cursor-pointer ${dk.inputOnCard}`} onClick={() => setShowProductDropdown(!showProductDropdown)} data-id="element-21945" data-mp="2p7a17xb3ydhs">
                  <span className={saleForm.productId ? dk.textPrimary : dk.textMuted} data-id="element-21946" data-mp="2ywp685345oth">
                    {getSelectedProductName() || 'Select a book...'}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 ${dk.textMuted}`} data-id="element-21947" data-mp="2pxvccmh63cjm" />
                </div>

                {showProductDropdown && <div className={`absolute z-10 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto border ${dk.card} ${dk.border}`} data-id="element-21948" data-mp="2g5fs1iieebvk">
                    <div className={`p-2 sticky top-0 border-b border-gray-100 dark:border-gray-700 ${dk.card}`} data-id="element-21949" data-mp="2759lkgl4ac5w">
                      <div className="relative" data-id="element-21950" data-mp="225n763tjypvi">
                        <SearchIcon className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${dk.textMuted}`} data-id="element-21951" data-mp="1x7dcdubqpraf" />
                        <input type="text" value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search by title or author..." className={`w-full p-2 pl-7 text-sm rounded-md border ${dk.inputOnCard}`} autoFocus data-id="element-21952" data-mp="cr5gwf3xdiaa" />
                      </div>
                    </div>
                    {filteredBooks.length > 0 ? filteredBooks.map(book => <div key={book.id} onClick={() => {
                setSaleForm({
                  ...saleForm,
                  productId: book.id,
                  productType: 'book'
                });
                setShowProductDropdown(false);
                setProductSearch('');
              }} className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b last:border-0 ${dk.border}`} data-id="element-21953" data-mp="2xoonfjfsb8q">
                          <div className={`font-medium text-sm ${dk.textPrimary}`} data-id="element-21954" data-mp="29r3cpkvyqfrk">
                            {book.title}
                          </div>
                          <div className={`text-xs ${dk.textMuted}`} data-id="element-21955" data-mp="2is1hw6i50tbq">
                            {book.author}
                          </div>
                        </div>) : <div className={`p-4 text-center text-sm ${dk.textMuted}`} data-id="element-21956" data-mp="2simg3stwfvu4">
                        No books found
                      </div>}
                  </div>}
              </div>
            </div>

            {/* Discount Type */}
            <div data-id="element-21957" data-mp="3l1ovx0gy84de">
              <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-21958" data-mp="3q2w430n075kl">
                Discount Type
              </label>
              <div className="flex gap-4 mb-4" data-id="element-21959" data-mp="3c2tpbn9zss9k">
                <button onClick={() => setSaleForm({
              ...saleForm,
              discountType: 'percentage'
            })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.discountType === 'percentage' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21960" data-mp="3h1oi9i1obx7e">
                  <PercentIcon className="w-4 h-4" data-id="element-21961" data-mp="2odsbfkie4oww" />
                  <span className="font-medium" data-id="element-21962" data-mp="2oflih0wx1b5f">Percentage Off</span>
                </button>
                <button onClick={() => setSaleForm({
              ...saleForm,
              discountType: 'fixed'
            })} className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${saleForm.discountType === 'fixed' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300' : `border-transparent bg-gray-100 dark:bg-gray-800 ${dk.textSecondary} hover:bg-gray-200 dark:hover:bg-gray-700`}`} data-id="element-21963" data-mp="2lrobi0uigr14">
                  <DollarSignIcon className="w-4 h-4" data-id="element-21964" data-mp="3egdgiayotgi5" />
                  <span className="font-medium" data-id="element-21965" data-mp="3ig44y80qrkji">Fixed Sale Price</span>
                </button>
              </div>

              {saleForm.discountType === 'percentage' ? <div data-id="element-21966" data-mp="3sds89mcey0da">
                  <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21967" data-mp="3sd8nlxvdoxvk">
                    Percentage Value
                  </label>
                  <div className="relative max-w-[200px]" data-id="element-21968" data-mp="1cyfl4busr4c">
                    <input type="number" value={saleForm.discountValue || ''} onChange={e => setSaleForm({
                ...saleForm,
                discountValue: parseFloat(e.target.value)
              })} className={`w-full p-2 pr-8 rounded-md border ${dk.inputOnCard}`} placeholder="20" data-id="element-21969" data-mp="1fkmr2uu14m1" />
                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${dk.textMuted}`} data-id="element-21970" data-mp="fawk7i0dh96n">
                      %
                    </span>
                  </div>
                </div> : <div className="grid grid-cols-2 gap-4" data-id="element-21971" data-mp="1321z9uyhnxfw">
                  <div data-id="element-21972" data-mp="2xaz3vyedt887">
                    <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21973" data-mp="2xbqmm8mvrbh2">
                      Sale Price
                    </label>
                    <div className="relative" data-id="element-21974" data-mp="2nlnusbrhw3kb">
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${dk.textMuted}`} data-id="element-21975" data-mp="2nitd2rzv8wul">
                        $
                      </span>
                      <input type="number" value={saleForm.discountValue || ''} onChange={e => setSaleForm({
                  ...saleForm,
                  discountValue: parseFloat(e.target.value)
                })} className={`w-full p-2 pl-7 rounded-md border ${dk.inputOnCard}`} placeholder="2.99" data-id="element-21976" data-mp="2emalpk7a9h04" />
                    </div>
                  </div>
                  <div data-id="element-21977" data-mp="1scc0cmdhrz65">
                    <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21978" data-mp="1s9fxcjmsekau">
                      Regular Price
                    </label>
                    <div className="relative" data-id="element-21979" data-mp="223eybfb4jy17">
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${dk.textMuted}`} data-id="element-21980" data-mp="1x9u8wbnlkcdm">
                        $
                      </span>
                      <input type="number" value={saleForm.regularPrice || ''} onChange={e => setSaleForm({
                  ...saleForm,
                  regularPrice: parseFloat(e.target.value)
                })} className={`w-full p-2 pl-7 rounded-md border ${dk.inputOnCard}`} placeholder="5.99" data-id="element-21981" data-mp="26zn8sfedn321" />
                    </div>
                  </div>
                </div>}
            </div>

            {/* Sale Dates */}
            <div data-id="element-21982" data-mp="x2p21bhp6hqq">
              <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-21983" data-mp="x25ixrgb9nb4">
                Sale Duration
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-21984" data-mp="11zfhbwloh9b3">
                <div data-id="element-21985" data-mp="4fummszqm8k4">
                  <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21986" data-mp="4c6t3tualm5e">
                    Sale Starts
                  </label>
                  <div className="relative" data-id="element-21987" data-mp="ea4f0aooj6uu">
                    <CalendarIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dk.textMuted}`} data-id="element-21988" data-mp="j3fqwq8cmdtm" />
                    <input type="date" value={saleForm.startDate || ''} onChange={e => setSaleForm({
                  ...saleForm,
                  startDate: e.target.value
                })} className={`w-full p-2 pl-9 rounded-md border ${dk.inputOnCard}`} data-id="element-21989" data-mp="3hqtmq0000mm3" />
                  </div>
                </div>
                <div data-id="element-21990" data-mp="1pwvfezqbqyq5">
                  <label className={`block text-xs font-medium mb-1 ${dk.textMuted}`} data-id="element-21991" data-mp="1ky0k4yb67hp5">
                    Sale Ends
                  </label>
                  <div className="relative" data-id="element-21992" data-mp="1g57f09bsi7cj">
                    <CalendarIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dk.textMuted}`} data-id="element-21993" data-mp="1c4xal13nbozs" />
                    <input type="date" value={saleForm.endDate || ''} onChange={e => setSaleForm({
                  ...saleForm,
                  endDate: e.target.value
                })} className={`w-full p-2 pl-9 rounded-md border ${dk.inputOnCard}`} data-id="element-21994" data-mp="174j3pmds6l3c" />
                  </div>
                </div>
              </div>
            </div>

            {/* Promo Tagline */}
            <div data-id="element-21995" data-mp="imsdik9luxs9">
              <div className="flex justify-between items-center mb-1" data-id="element-21996" data-mp="im95bv9rbnrf">
                <label className={`block text-sm font-medium ${dk.textSecondary}`} data-id="element-21997" data-mp="16b1xrhr67dha">
                  Promo Tagline{' '}
                  <span className={`text-xs font-normal ${dk.textMuted}`} data-id="element-21998" data-mp="1bc92t91xhyk9">
                    (Optional)
                  </span>
                </label>
                <span className={`text-xs ${dk.textMuted}`} data-id="element-21999" data-mp="11jadtq2is604">
                  {saleForm.tagline?.length || 0}/120
                </span>
              </div>
              <input type="text" value={saleForm.tagline || ''} onChange={e => {
            if (e.target.value.length <= 120) {
              setSaleForm({
                ...saleForm,
                tagline: e.target.value
              });
            }
          }} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. Grab it before it's gone — 48 hours only!" data-id="element-22000" data-mp="1u0nt1u4f98k0" />
              <p className={`mt-1 text-xs ${dk.textMuted}`} data-id="element-22001" data-mp="2vti86pf0wo0j">
                This will appear on your Trove listing to catch readers'
                attention.
              </p>
            </div>
          </>}
      </div>
    </div>;
};